// V3 shell — "Bento" card-first layout.
//
//   ┌──┬──────────────────────────────────────────┐
//   │ ◆│ Dashboard · breadcrumb   🔍 V3 ☀ ⚙       │
//   │  ├──────────────────────────────────────────┤
//   │  │                                          │
//   │ □│  ┌──────────┐ ┌──────────────────────┐  │
//   │ □│  │ tile     │ │ tile (wide)          │  │
//   │ □│  └──────────┘ └──────────────────────┘  │
//   │ □│  ┌──────────────────────────────────┐    │
//   │ □│  │ tile (full width)                │    │
//   │  │  └──────────────────────────────────┘    │
//   │  │                                          │
//   │ ⏻│                                          │
//   └──┴──────────────────────────────────────────┘
//
// Left "rail" is an icon-only column (64px wide on desktop, hidden on mobile
// behind a drawer). The page area is full width with a slim breadcrumb +
// controls strip at the top. Pages aren't restructured here — Panels render
// inside and get bento-tile styling via the [data-version="v3"] CSS layer.

import React, { useEffect, useState, useCallback } from "react";
import { LogOut, Menu, Search, Sun, Moon, X } from "lucide-react";
import { loadState, logout } from "../api";
import { nav, can } from "../nav";
import Module from "./Module";
import WelcomeOverlay from "./WelcomeOverlay";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

export default function ShellV3({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const unreadMessages = useUnreadMessages({ active, onNavigate: setActive });
  const [error, setError]     = useState("");
  function navigate(key, arg = null) {
    setActive(key);
    setNavArg(arg);
  }
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();

  const [showWelcome, setShowWelcome] = useState(
    () => sessionStorage.getItem("gabruz-just-logged-in") === "1"
  );
  useEffect(() => {
    if (showWelcome) sessionStorage.removeItem("gabruz-just-logged-in");
  }, [showWelcome]);

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [reload]);

  const allowed = nav.filter(n => can(user, n.key));
  const activeItem = allowed.find(n => n.key === active);

  function signOut() {
    logout();
    setUser(null);
  }
  function navigateTo(key) {
    setActive(key);
    setDrawerOpen(false);
  }

  const initial = (user.name || "?")[0]?.toUpperCase() || "?";

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
    <div className={`appV3${showWelcome ? " app--welcoming" : ""}`}>
      {showWelcome && <WelcomeOverlay onDone={() => setShowWelcome(false)} />}

      {/* ----- Left icon rail (desktop) ----- */}
      <aside className="appV3-rail">
        <a className="appV3-rail-brand" href="#" onClick={(e) => { e.preventDefault(); navigateTo("dashboard"); }} title="Gabruz Universe">
          <img src="/logo.png" onError={e => e.currentTarget.style.display = "none"} alt="" />
        </a>
        <nav className="appV3-rail-nav" aria-label="Main">
          {allowed.map(item => {
            const Icon = item.icon;
            const itemBadge = item.key === "messages" && unreadMessages > 0
              ? unreadMessages : null;
            return (
              <button
                key={item.key}
                type="button"
                className={active === item.key ? "appV3-rail-btn appV3-rail-btn--active" : "appV3-rail-btn"}
                onClick={() => navigateTo(item.key)}
                title={item.label}
                aria-label={item.label}
              >
                <span className="appV3-rail-iconWrap">
                  <Icon size={18} />
                  {itemBadge && (
                    <span className="appV3-rail-badge">{itemBadge > 99 ? "99+" : itemBadge}</span>
                  )}
                </span>
                <span className="appV3-rail-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="appV3-rail-btn appV3-rail-out" onClick={signOut} title="Sign out" aria-label="Sign out">
          <LogOut size={18} />
          <span className="appV3-rail-label">Sign Out</span>
        </button>
      </aside>

      {/* ----- Right column: top strip + bento page ----- */}
      <div className="appV3-content">
        <header className="appV3-top">
          <button className="appV3-burger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <Menu size={18} />
          </button>
          <NotificationBell notifications={state.notifications} onNavigate={navigateTo}/>
          <div className="appV3-crumb">
            <span className="appV3-crumb-trail">Gabruz Universe ·</span>
            <span className="appV3-crumb-active">{activeItem?.label || "Dashboard"}</span>
          </div>

          <TeamSwitcher />

          <div className="appV3-search">
            <Search size={15} />
            <input placeholder="Search…" />
          </div>

          <div className="versionSwitch" role="radiogroup" aria-label="UI design version">
            <button type="button" className={version === "v1" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v1")} role="radio" aria-checked={version === "v1"} title="Original UI">V1</button>
            <button type="button" className={version === "v2" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v2")} role="radio" aria-checked={version === "v2"} title="Aurora UI">V2</button>
            <button type="button" className={version === "v3" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v3")} role="radio" aria-checked={version === "v3"} title="Bento UI">V3</button>
            <button type="button" className={version === "v4" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v4")} role="radio" aria-checked={version === "v4"} title="Mobile UI">V4</button>
          </div>

          <button type="button" className="themeToggle" onClick={toggleTheme} title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"} aria-label="Toggle color theme">
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          <div className="appV3-me" title={user.email}>
            <div className="appV3-me-avatar">{user.avatar || initial}</div>
            <div className="appV3-me-text">
              <strong>{user.name}</strong>
              <span>{user.roles[0].replace("_", " ")}</span>
            </div>
          </div>
        </header>

        <main className="appV3-main">
          <div className="page page--v3">
            {loading ? (
              <div className="empty">
                <h2>Loading…</h2>
                <p>Fetching data from the API.</p>
              </div>
            ) : error ? (
              <div className="empty">
                <h2>Couldn't load data</h2>
                <p>{error}</p>
                <button className="primary" onClick={reload}>Retry</button>
              </div>
            ) : (
              <Module active={active} setActive={navigate} navArg={navArg} state={state} reload={reload} user={user} setUser={setUser} />
            )}
          </div>
        </main>
      </div>

      {/* ----- Mobile slide-in drawer ----- */}
      {drawerOpen && (
        <>
          <div className="appV3-drawer-veil" onClick={() => setDrawerOpen(false)} />
          <aside className="appV3-drawer">
            <div className="appV3-drawer-head">
              <strong>Navigation</strong>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"><X size={18}/></button>
            </div>
            {allowed.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={active === item.key ? "appV3-drawer-link appV3-drawer-link--active" : "appV3-drawer-link"}
                  onClick={() => navigateTo(item.key)}
                >
                  <Icon size={16} /><span>{item.label}</span>
                </button>
              );
            })}
            <button className="appV3-drawer-link appV3-drawer-out" onClick={signOut}>
              <LogOut size={16} /> Sign Out
            </button>
          </aside>
        </>
      )}
      <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />
    </div>
    </ActiveTeamProvider>
  );
}
